import React, { useState, useRef, useEffect } from "react";
import moment from "moment-timezone";
import { TriangleAlertIcon, ChevronDown, ChevronRight } from "lucide-react";
import {
  useFormV2,
  FormField,
  FormGrid,
  FormActions,
} from "@/lib/form-v2";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { DialogClose } from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TradingAccount } from "@/lib/api/otc";
import { Customer } from "@/lib/api/reports";
import { getActiveValidator } from "@/lib/form-v2";
import { useListTraders } from "@/lib/api/trader";
import { getOptionMonthComboBase } from "@/lib/api/common";
import { Expiration } from "@lhc/commodities/dates";
import {
  UpdateContractInfo,
  ContractFuturesStep,
  getStepsForContractType,
  isCanolaContractWithFuturesRows,
} from "../OtcBearState";
import { OtcFormValues } from "./types";
import { otcSchemaConfig } from "./schema-config";
import { buildFieldMeta } from "./field-meta";
import { formToApi } from "./transform";
import {
  isReownContractType,
  parseContractRefGroups,
  serializeContractRefGroups,
  stripContractRefDelimiters,
  validateContractRefGroups,
  ContractRefGroup,
} from "./contract-reference-utils";
import { ReownContractReferenceEditor } from "./ReownContractReferenceEditor";
import {
  computeOtcFormWarnings,
  MarginWarning,
  MarginWarningField,
  StepWarning,
} from "./warnings";
import {
  marginWarningTooltipText,
  stepWarningTooltipText,
} from "./warnings-format";
import { WarningConfirmDialog } from "./WarningConfirmDialog";

// ---------------------------------------------------------------------------
// Expiration warning helper
// ---------------------------------------------------------------------------

function useExpirationWarning(values: OtcFormValues) {
  let differs = false;
  let expectedDate: Date | undefined;

  if (values.option_month && values.end_date) {
    try {
      const optResult = getOptionMonthComboBase(values.option_month);
      const computed = Expiration.calculateLastTradingDay(
        optResult.firstDayOfMonthMoment,
      );
      expectedDate = computed;
      if (computed) {
        differs = !moment(values.end_date as Date)
          .endOf("day")
          .isSame(moment(computed).endOf("day"));
      }
    } catch {
      // ignore
    }
  }

  return { differs, expectedDate };
}

// ---------------------------------------------------------------------------
// Actual Contract Futures collapsed-preview helpers
// ---------------------------------------------------------------------------

const ACTUAL_FUTURES_FIELD_BY_STEP: Record<ContractFuturesStep, keyof OtcFormValues> = {
  step_one: "actual_contract_futures_step_one",
  step_two: "actual_contract_futures_step_two",
  step_three: "actual_contract_futures",
  double_up: "actual_contract_futures_double_up",
};

// Builds the compact preview string shown when the futures rows are collapsed.
// e.g. for Range with one missing quoted value: "0.2500 × (Empty) × 0.3540".
// Empty positions render as "(Empty)".
function buildFuturesCollapsedPreview(values: OtcFormValues): string {
  const steps = getStepsForContractType(values.contract_type as string | undefined);
  if (steps.length === 0) return "";
  const parts = steps.map((step: ContractFuturesStep) => {
    const raw = values[ACTUAL_FUTURES_FIELD_BY_STEP[step]];
    if (raw == null || (typeof raw === "string" && raw.trim() === "")) return "(Empty)";
    const num = Number(raw);
    if (Number.isNaN(num)) return "(Empty)";
    return num.toFixed(4);
  });
  return parts.join(" × ");
}

// ---------------------------------------------------------------------------
// Adjusted-row collapse helpers
// ---------------------------------------------------------------------------

/** Fields that indicate user-entered data for each adjusted row (excludes derived fields). */
const ADJ_ROW_DATA_FIELDS: string[][] = [
  ["adj_option_premium", "adj_contract_fee", "last_adjusted_date"],
  ["adj_option_premium_two", "adj_contract_fee_two", "adjusted_date_two"],
  ["adj_option_premium_three", "adj_contract_fee_three", "adjusted_date_three"],
];

/**
 * Returns the highest 1-indexed adjusted row that contains data.
 * 0 means no adjusted row has data.
 */
function highestAdjRowWithData(values: OtcFormValues): number {
  let highest = 0;
  for (let i = 0; i < ADJ_ROW_DATA_FIELDS.length; i++) {
    const hasData = ADJ_ROW_DATA_FIELDS[i].some((key) => {
      const v = values[key];
      if (v === undefined || v === null || v === "") return false;
      if (typeof v === "string" && v.trim() === "") return false;
      return true;
    });
    if (hasData) highest = i + 1;
  }
  return highest;
}

/**
 * Build a label like "Adj Row 3 (No Data)" or "Adj Row 2 & 3 (No Data)"
 * for the collapsed rows.
 */
function collapsedAdjLabel(hiddenRowNumbers: number[]): string {
  if (hiddenRowNumbers.length === 0) return "";
  const nums = hiddenRowNumbers.join(" & ");
  return `Adj Row ${nums} (No Data)`;
}

// ---------------------------------------------------------------------------
// OtcContractFormV2
// ---------------------------------------------------------------------------

export function OtcContractFormV2({
  isPreviewOnly,
  accounts,
  customers,
  isUpdateForm,
  initialValues,
  onSubmit,
  isSubmitting,
}: {
  isPreviewOnly?: boolean;
  accounts: TradingAccount[];
  customers: Customer[];
  isUpdateForm: boolean;
  initialValues: OtcFormValues;
  onSubmit: (formData: UpdateContractInfo) => Promise<boolean>;
  /**
   * True while the parent is in the middle of saving. Disables the submit
   * button so warnings (or normal submits) cannot double-fire. Defaults to
   * false when the parent has not wired it.
   */
  isSubmitting?: boolean;
}) {
  const listTradersRes = useListTraders();
  const traders = listTradersRes?.data?.data || [];

  const fieldMeta = buildFieldMeta({
    accounts,
    customers,
    traders,
    isPreviewOnly,
  });

  const form = useFormV2<OtcFormValues>(
    otcSchemaConfig,
    fieldMeta,
    initialValues,
  );

  const { differs: expirationDiffers, expectedDate } = useExpirationWarning(form.values);

  // Adjusted rows: compute which rows have data and default-expand accordingly
  // Not on initial values but always latest so the user can always hide and be aware
  const highestWithData = highestAdjRowWithData(form.values as any);
  const [adjRowsExpanded, setAdjRowsExpanded] = useState(false);

  // Actual Contract Futures rows: expanded by default. Only shown for canola
  // contract types that have at least one applicable step. Rows are gated per
  // step by getStepsForContractType in field-meta visibility predicates.
  const [futuresRowsExpanded, setFuturesRowsExpanded] = useState(true);
  const futuresSectionVisible = isCanolaContractWithFuturesRows({
    commodity: form.values.commodity as string | undefined,
    contract_type: form.values.contract_type as string | undefined,
  });
  const futuresSteps = getStepsForContractType(form.values.contract_type as string | undefined);

  // Reown contract reference multi-group state
  const [refGroups, setRefGroups] = useState<ContractRefGroup[]>(() =>
    parseContractRefGroups(initialValues.contract_reference),
  );

  // Reown contract reference helpers
  const isReown = isReownContractType(form.values.contract_type as string | undefined);
  const wasReownRef = useRef(isReown);

  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [isLocalSubmitInFlight, setIsLocalSubmitInFlight] = useState(false);
  const submitInFlightRef = useRef(false);
  const submitDisabled = !!isSubmitting || isLocalSubmitInFlight;

  // When switching away from a reown type, strip the delimiters from
  // contract_reference so the plain text field gets a clean value.
  useEffect(() => {
    if (wasReownRef.current && !isReown) {
      const plain = stripContractRefDelimiters(form.values.contract_reference);
      form.setFieldValue("contract_reference", plain || undefined);
      setRefGroups([{ ref: "", qty: "" }]);
    }
    wasReownRef.current = isReown;
  }, [isReown]);

  if ([listTradersRes].some((r) => r.isLoading)) {
    return (
      <div className="flex flex-col space-y-2">
        <Skeleton className="h-4 w-[250px]" />
        <Skeleton className="h-4 w-[200px]" />
      </div>
    );
  }

  // ---- Submit ----

  const actuallySubmit = async (): Promise<boolean> => {
    if (submitInFlightRef.current || isSubmitting) return false;

    submitInFlightRef.current = true;
    setIsLocalSubmitInFlight(true);

    try {
      const validator = getActiveValidator(otcSchemaConfig, form.values.contract_type as string | undefined);
      const apiData = formToApi(form.values, validator);
      return await onSubmit(apiData as unknown as UpdateContractInfo);
    } finally {
      submitInFlightRef.current = false;
      setIsLocalSubmitInFlight(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPreviewOnly) return;
    if (submitDisabled || submitInFlightRef.current) return;

    form.attemptSubmit();
    const result = form.validate();
    if (!result.success) return;

    if (warnings.hasAny) {
      setWarningDialogOpen(true);
      return;
    }

    await actuallySubmit();
  };

  const handleWarningConfirm = async (): Promise<void> => {
    // Bypass the warning gate to avoid reopening the same dialog.
    const submitted = await actuallySubmit();
    if (submitted) setWarningDialogOpen(false);
  };

  // ---- Visibility helpers ----

  const isVisible = (name: string): boolean => {
    const props = form.getFieldProps(name);
    return !!props?.visible;
  };

  /**
   * Whether an adjusted margin field's row is currently rendered in the form.
   * Adjusted rows are hidden by the local collapse state when they have no
   * data and the user has not expanded them, so field-meta visibility alone
   * is not enough to decide whether to surface a warning.
   */
  const isAdjustedMarginRendered = (name: MarginWarningField): boolean => {
    if (adjRowsExpanded) return true;
    if (name === "adj_margin_amount") return highestWithData >= 1;
    if (name === "adj_margin_amount_two") return highestWithData >= 2;
    if (name === "adj_margin_amount_three") return highestWithData >= 3;
    return false;
  };

  /**
   * Whether a margin warning field is rendered such that an inline icon/ring
   * can attach to it. Canola futures margin fields use field-meta visibility
   * even when the local "Quoted Contract Futures" section is collapsed — the
   * warning still belongs to the field; only its inline UI is hidden until
   * expanded.
   */
  const isMarginFieldRendered = (name: MarginWarningField): boolean => {
    if (
      name === "adj_margin_amount" ||
      name === "adj_margin_amount_two" ||
      name === "adj_margin_amount_three"
    ) {
      return isAdjustedMarginRendered(name);
    }
    return isVisible(name);
  };

  // ---- Warnings ----

  const warnings = computeOtcFormWarnings({
    values: form.values,
    isFieldVisible: isVisible,
    isMarginFieldRendered,
    getFieldLabel: (name: string) => form.getFieldProps(name)?.label ?? name,
  });

  const warningsByName = new Map<string, Array<MarginWarning | StepWarning>>();
  for (const w of warnings.margin) {
    const list = warningsByName.get(w.field) ?? [];
    list.push(w);
    warningsByName.set(w.field, list);
  }
  for (const w of warnings.step) {
    const list = warningsByName.get(w.field) ?? [];
    list.push(w);
    warningsByName.set(w.field, list);
  }

  const buildWarningTooltip = (
    items: Array<MarginWarning | StepWarning>,
  ): React.ReactNode => {
    const lines: string[] = items.map((item: MarginWarning | StepWarning) => {
      if ("threshold" in item) {
        return marginWarningTooltipText(item);
      }
      return stepWarningTooltipText(item);
    });
    return (
      <div className="flex flex-col gap-1">
        {lines.map((line: string, i: number) => (
          <p key={i}>{line}</p>
        ))}
      </div>
    );
  };

  const warningExtraFor = (name: string): React.ReactNode | undefined => {
    const items = warningsByName.get(name);
    if (!items || items.length === 0) return undefined;
    return (
      <Tooltip>
        <TooltipTrigger type="button">
          <TriangleAlertIcon
            className="h-4 w-4 text-orange-400"
            data-testid={`form-warning-icon-${name}`}
          />
        </TooltipTrigger>
        <TooltipContent>{buildWarningTooltip(items)}</TooltipContent>
      </Tooltip>
    );
  };

  // ---- Render helpers ----

  const renderField = (name: string, extra?: React.ReactNode) => {
    const props = form.getFieldProps(name);
    if (!props || !props.visible) return null;

    const warningExtra = warningExtraFor(name);
    const composedExtra: React.ReactNode = warningExtra
      ? (
        <span className="flex items-center gap-1">
          {extra}
          {warningExtra}
        </span>
      )
      : extra;

    return (
      <FormField
        key={name}
        fieldProps={props}
        onChange={(value) => form.setFieldValue(name, value)}
        onBlur={() => form.touchField(name)}
        extra={composedExtra}
        warning={!!warningExtra}
      />
    );
  };

  const handleRefGroupsChange = (groups: ContractRefGroup[]) => {
    setRefGroups(groups);
    const serialized = serializeContractRefGroups(groups);
    form.setFieldValue("contract_reference", serialized || undefined);
  };

  // Expiration date extra tooltip
  const expirationExtra = expirationDiffers ? (
    <Tooltip>
      <TooltipTrigger>
        <TriangleAlertIcon className="h-4 w-4 text-orange-400" />
      </TooltipTrigger>
      <TooltipContent>
        <p>Expected an end date of {moment(expectedDate).format("MM/DD/YYYY")}</p>
      </TooltipContent>
    </Tooltip>
  ) : null;

  return (
    <form aria-disabled={isPreviewOnly} onSubmit={handleSubmit}>
      <div className="flex flex-col md:flex md:flex-col items-start gap-4 content-center">
        {/* Row 1 */}
        <FormGrid cols={5}>
          {renderField("date")}
          {renderField("end_date", expirationExtra)}
          {renderField("contract_type")}
          {renderField("customer_id")}
          {renderField("commodity")}
        </FormGrid>

        {/* Row 2 */}
        <FormGrid cols={6}>
          {renderField("option_month")}
          {renderField("quantity")}
          {renderField("trade_reference")}
          {renderField("account_no_a")}
          {renderField("has_invoice_fees")}
          {renderField("has_contract_adjustment")}
        </FormGrid>

        {/* Steps row – only visible fields render */}
        <FormGrid cols={6}>
          {renderField("step_one")}
          {renderField("step_two")}
          {renderField("step_three")}
          {renderField("double_up")}
          {renderField("implied_fx_amount")}
        </FormGrid>

        {/* Actual Contract Futures section (Canola only). Collapsible toggle,
            expanded by default. One 3-col row per applicable step for the
            current contract type. */}
        {futuresSectionVisible && (
          <div className="flex flex-col w-full gap-4">
            <button
              type="button"
              data-testid="actual-contract-futures-toggle"
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors font-bold"
              onClick={() => setFuturesRowsExpanded((prev) => !prev)}
            >
              {futuresRowsExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {futuresRowsExpanded
                ? "Collapse quoted contract futures"
                : `Quoted Contract Futures: ${buildFuturesCollapsedPreview(form.values)}`}
            </button>
            {futuresRowsExpanded && futuresSteps.map((step: ContractFuturesStep) => {
              const suffix = step === "step_three" ? "" : `_${step}`;
              return (
                <FormGrid cols={3} key={step}>
                  {renderField(`expected_contract_futures${suffix}`)}
                  {renderField(`actual_contract_futures${suffix}`)}
                  {renderField(`futures_margin${suffix}`)}
                </FormGrid>
              );
            })}
            {futuresRowsExpanded && (
              <FormGrid cols={4}>
                {renderField("final_futures_expected_price")}
                {renderField("final_futures_price")}
                {renderField("final_futures_price_date")}
                {renderField("final_futures_price_margin")}
              </FormGrid>
            )}
          </div>
        )}

        {/* Premium Offer WDU row */}
        {isVisible("account_no_b") && (
          <FormGrid cols={5}>
            {renderField("account_no_b")}
            {renderField("futures_price")}
            {renderField("otc_futures")}
          </FormGrid>
        )}

        {/* Financials */}
        <FormGrid cols={6}>
          {renderField("option_premium")}
          {renderField("contract_fee")}
          {renderField("margin")}
          {renderField("margin_amount")}
          {renderField("contract_no")}
          {!isReown && renderField("contract_reference")}
        </FormGrid>

        {/* Reown contract references (multi-group) */}
        {isReown && isVisible("contract_reference") && (
          <ReownContractReferenceEditor
            groups={refGroups}
            onChange={handleRefGroupsChange}
            disabled={isPreviewOnly}
            errors={validateContractRefGroups(refGroups, { requireFirstGroup: false })}
          />
        )}

        {/* Collapsed adjusted rows toggle */}
        {(() => {
          const hiddenRows: number[] = [];
          if (highestWithData < 1 && !adjRowsExpanded) hiddenRows.push(1);
          if (highestWithData < 2 && !adjRowsExpanded) hiddenRows.push(2);
          if (highestWithData < 3 && !adjRowsExpanded) hiddenRows.push(3);

          if (hiddenRows.length === 0 && !adjRowsExpanded) return null;

          return (
            <button
              type="button"
              data-testid="adj-rows-toggle"
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors font-bold"
              onClick={() => setAdjRowsExpanded((prev) => !prev)}
            >
              {adjRowsExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {adjRowsExpanded
                ? "Collapse adjusted rows"
                : collapsedAdjLabel(hiddenRows)}
            </button>
          );
        })()}

        {/* Adjusted 1 */}
        {(highestWithData >= 1 || adjRowsExpanded) && (
          <FormGrid cols={5}>
            {renderField("adj_option_premium")}
            {renderField("adj_contract_fee")}
            {renderField("adj_margin")}
            {renderField("adj_margin_amount")}
            {renderField("last_adjusted_date")}
          </FormGrid>
        )}

        {/* Adjusted 2 */}
        {(highestWithData >= 2 || adjRowsExpanded) && (
          <FormGrid cols={5}>
            {renderField("adj_option_premium_two")}
            {renderField("adj_contract_fee_two")}
            {renderField("adj_margin_two")}
            {renderField("adj_margin_amount_two")}
            {renderField("adjusted_date_two")}
          </FormGrid>
        )}

        {/* Adjusted 3 */}
        {(highestWithData >= 3 || adjRowsExpanded) && (
          <FormGrid cols={5}>
            {renderField("adj_option_premium_three")}
            {renderField("adj_contract_fee_three")}
            {renderField("adj_margin_three")}
            {renderField("adj_margin_amount_three")}
            {renderField("adjusted_date_three")}
          </FormGrid>
        )}

        {/* Accumulator */}
        {isVisible("start_date") && (
          <FormGrid cols={4}>
            {renderField("start_date")}
            {renderField("weeks")}
            {renderField("period_qty")}
            {renderField("knockout")}
          </FormGrid>
        )}

        {/* Shipment / Trader */}
        <FormGrid cols={5}>
          {renderField("shipment_start_date")}
          {renderField("shipment_end_date")}
          {renderField("trader_id")}
          {renderField("crop_year")}
          {renderField("rolled_option_month")}
        </FormGrid>

        {/* Carry / Basis / Notes */}
        <FormGrid cols={5}>
          {renderField("carry")}
          {renderField("rolled_basis_month")}
          {renderField("basis_carry")}
          {renderField("notes")}
          {renderField("option_month_etc_one")}
        </FormGrid>

        {/* Warning confirmation dialog */}
        <WarningConfirmDialog
          open={warningDialogOpen}
          onOpenChange={setWarningDialogOpen}
          warnings={warnings}
          isUpdateForm={isUpdateForm}
          onConfirm={handleWarningConfirm}
          isSubmitting={submitDisabled}
        />

        {/* Actions */}
        <FormActions>
          <DialogClose asChild>
            <Button type="button" variant="secondary" className="col-start-1">
              Close
            </Button>
          </DialogClose>

          {!isPreviewOnly && (
            <Button
              type="submit"
              size="sm"
              className="px-3 col-start-4"
              disabled={!form.isValid || submitDisabled}
              data-testid="form-submit"
            >
              {isUpdateForm ? "Update" : "Create"}
            </Button>
          )}
        </FormActions>
      </div>
    </form>
  );
}
